import React, { Suspense } from 'react'
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom'
import RoleUsers from '../Components/MangeRole/RoleEditor/RoleUsers'
import Loader from '../Components/Shared/Loader'
import { PAGES_ROUTES } from '../Constants/PagesRoutes'
import { PAGE_URL, PageNames } from '../Constants/PagesRoutes/PageURLs'
import DefaultLayout from '../Layouts/DefaultLayout'
import SearchAdvanced from '../Pages/AdvancedSearch'
import CreateUpdateAttribute from '../Pages/AttributeRepository/Editor'
import Helpdesk from '../Pages/HelpDesk'
import Home from '../Pages/Home'
import HomeAccessError from '../Pages/Home/HomeAccessError'
import BulkImageUpload from '../Pages/ManageContent/BulkImageUpload'
import MastersList from '../Pages/Masters'
import CreateSystemConfiguration from '../Pages/Masters/SystemConfiguration/Edit/CreateSystemConfigration'
import CreatePartners from '../Pages/Partners'
import ReportsList from '../Pages/ReportsPage'
import SaveSearchList from '../Pages/SaveSearchList'
import { authenticationGuard } from './auth'
const NotFound = React.lazy(() => import('../Pages/Erros/404'))
const ErrorFound = React.lazy(() => import('../Pages/Erros/500'))
const AccessDeniedComponent = React.lazy(() => import('../Pages/AccessDenied'))
const ComingSoonComponent = React.lazy(() => import('../Pages/ComingSoon'))
const InternalServerErrorComponent = React.lazy(() => import('../Pages/InternalServerError'))
const Login = React.lazy(() => import('../Pages/Login'))
const AuthCallback = React.lazy(() => import('../Pages/Auth'))
const CommonListing = React.lazy(() => import('../Components/CommonListing'))
const CreateUpdateRole = React.lazy(() => import('../Pages/ManageRole/CreateUpdateRole'))
const CreateUpdateUser = React.lazy(() => import('../Pages/ManageUser/CreateUpdateUser'))
const AssetTypeDefinitonMain = React.lazy(() => import('../Pages/AssetTypeDefiniton/Editor'))
const AssociatedAsset = React.lazy(() => import('../Pages/AssociatedAssets'))
const PublishingMonitoringReport = React.lazy(
  () => import('../Pages/ReportsPage/PublishingMonitoring'),
)
const SNPReviewReportDetails = React.lazy(
  () => import('../Pages/ReportsPage/SNPReviewReportDetails'),
)
const AuditTrailsList = React.lazy(() => import('../Pages/AuditTrails/AuditTrailsList'))
const CommonAuditLogsList = React.lazy(() => import('../Pages/AuditTrails/AuditTrailsListing'))
const CommonAuditLogsDetails = React.lazy(() => import('../Components/CommonAuditDetails'))
const AssetListingComponent = React.lazy(
  () => import('../Pages/ManageContent/AssetListingComponent'),
)
const AuditTrailsListDetailPage = React.lazy(
  () => import('../Components/ManageContent/ManageAsset/AssetEditor/AuditTrailsListDetailPage'),
)
const ApplicablePolicyListPage = React.lazy(
  () => import('../Components/ManageContent/ManageAsset/AssetEditor/ApplicablePolicyListPage'),
)
const CreateUpdateAsset = React.lazy(() => import('../Pages/ManageContent/CreateUpdateAsset'))
const CreateUpdateGeoPolicy = React.lazy(
  () => import('../Pages/ManagePolicies/CreateUpdateGeoPolicy'),
)
const CreateUpdateControlPolicy = React.lazy(
  () => import('../Pages/ManagePolicies/CreateUpdateControlPolicy'),
)
const CreateUpdateAdBreakPolicy = React.lazy(
  () => import('../Pages/ManagePolicies/CreateUpdateAdBreakPolicy'),
)
const AssetStatusListing = React.lazy(() => import('../Components/Home/AssetStatusListing'))
const BulkUpdate = React.lazy(() => import('../Pages/BulkUpdate'))
const ManageFiles = React.lazy(() => import('../Pages/ManageFiles'))
const AllNotifications = React.lazy(() => import('../Pages/Notifications'))
const ManageTips = React.lazy(() => import('../Pages/ManageTips'))
const DefaultMasterEditorPage = React.lazy(() => import('../Pages/Masters/DefaultMaster'))
const {
  AccessDenied,
  ComingSoon,
  InternalServerError,
  AdvancedSearch,
  MetaMaster,
  Masters,
  CreateSystemConfigurationPageType,
  CreateSystemConfirgurationPage,
} = PAGES_ROUTES

export const routes: RouteObject[] = [
  {
    path: PAGE_URL[PageNames.Login],
    element: (
      <Suspense fallback={<Loader />}>
        <Login />
      </Suspense>
    ),
    errorElement: <ErrorFound />,
  },
  {
    path: PAGE_URL[PageNames.AuthValidate],
    element: (
      <Suspense fallback={<Loader />}>
        <AuthCallback />
      </Suspense>
    ),
    errorElement: <ErrorFound />,
  },
  {
    path: PAGE_URL[PageNames.ErrorPage],
    element: (
      <Suspense fallback={<Loader />}>
        <InternalServerErrorComponent />
      </Suspense>
    ),
  },
  {
    path: PAGE_URL[PageNames.Root],
    element: <DefaultLayout isPlainLayout />,
    errorElement: <NotFound />,
    children: [
      { path: PAGE_URL[PageNames.AllNotifications], element: <AllNotifications /> },
      {
        path: PAGE_URL[PageNames.AssetStateDetails],
        children: [
          { path: PAGE_URL[PageNames.AssetStateDetailsAdvanceSearch], element: <SearchAdvanced /> },
          { path: PAGE_URL[PageNames.AssetStateDetailsSaveSearch], element: <SaveSearchList /> },
          { path: PAGE_URL[PageNames.AssetStateDetailsState], element: <AssetStatusListing /> },
          {
            path: PAGE_URL[PageNames.AssetStateDetailsStateAccessDenied],
            element: <HomeAccessError />,
          },
        ],
      },
      {
        path: PAGES_ROUTES.Asset,
        children: [
          {
            path: `:pageType/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory/${PAGES_ROUTES.AuditTrails}/:contentIdNumber/:blitzId`,
            element: <AuditTrailsList />,
            loader: authenticationGuard,
          },
          {
            path: `:pageType/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory/${PAGES_ROUTES.AuditTrails}/:contentIdNumber/:blitzId/view/:auditId`,
            element: <AuditTrailsListDetailPage />,
            loader: authenticationGuard,
          },
          {
            path: `:pageType/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory/${PAGES_ROUTES.ApplicablePolicy}/:contentIdNumber/:blitzId`,
            element: <ApplicablePolicyListPage />,
            loader: authenticationGuard,
          },
          {
            path: `:pageType/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory/${PAGES_ROUTES.AssociatedAssets}/:assetId`,
            element: <AssociatedAsset />,
          },
          {
            path: `:pageType/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory/${PAGES_ROUTES.LanguageVariants}/:assetId`,
            element: <AssociatedAsset />,
          },
          {
            path: ':pageType/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory',
            element: <CreateUpdateAsset />,
          },
          {
            path: 'bulk-image-upload/:contentId/:assetType/:conCategory/:assetCategory/:metaCategory',
            element: <BulkImageUpload />,
          },
        ],
      },
      { path: '/', element: <Navigate to='/home' replace /> },
    ],
  },
  {
    path: PAGE_URL[PageNames.Root],
    element: <DefaultLayout />,
    errorElement: <ErrorFound />,
    children: [
      { path: PAGE_URL[PageNames.Home], element: <Home />, loader: authenticationGuard },

      /**
       * * * * * * * * * * * * * * * * * * * * * * Roles Routes Start * * * * * * * * * * * * * * * * * * * * * *
       */
      { path: PAGE_URL[PageNames.Role], element: <CommonListing />, loader: authenticationGuard },
      {
        path: PAGE_URL[PageNames.RoleCreate],
        element: <CreateUpdateRole />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.RoleViewEdit],
        element: <CreateUpdateRole />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.RoleAssociatedUsers],
        element: <RoleUsers />,
        loader: authenticationGuard,
      },

      /**
       * * * * * * * * * * * * * * * * * * * * * * Roles Routes End * * * * * * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * Users Routes Start * * * * * * * * * * * * * * * * * * * * * *
       */
      { path: PAGE_URL[PageNames.User], element: <CommonListing />, loader: authenticationGuard },
      {
        path: PAGE_URL[PageNames.UserCreate],
        element: <CreateUpdateUser />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.UserEdit],
        element: <CreateUpdateUser />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.UserView],
        element: <CreateUpdateUser />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * Users Routes End * * * * * * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * Attribute Repo Routes Start * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.AttributeRepo],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AttributeRepoCreate],
        element: <CreateUpdateAttribute />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AttributeRepoViewEdit],
        element: <CreateUpdateAttribute />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * Attribute Repo Routes End * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * Asset Defination Routes Start * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.AssetsDefination],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AssetsDefinationCreate],
        element: <AssetTypeDefinitonMain />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AssetsDefinationViewEdit],
        element: <AssetTypeDefinitonMain />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * Asset Defination Routes End * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Manage Policies Routes Start * * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.GeoPolicy],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      { path: PAGE_URL[PageNames.GeoPolicyCreate], element: <CreateUpdateGeoPolicy /> },
      { path: PAGE_URL[PageNames.GeoPolicyViewEdit], element: <CreateUpdateGeoPolicy /> },
      {
        path: PAGE_URL[PageNames.EntitlementControlPolicy],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.EntitlementControlPolicyCreate],
        element: <CreateUpdateControlPolicy />,
      },
      {
        path: PAGE_URL[PageNames.EntitlementControlPolicyViewEdit],
        element: <CreateUpdateControlPolicy />,
      },
      {
        path: PAGE_URL[PageNames.LanguageControlPolicy],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.LanguageControlPolicyCreate],
        element: <CreateUpdateControlPolicy />,
      },
      {
        path: PAGE_URL[PageNames.LanguageControlPolicyViewEdit],
        element: <CreateUpdateControlPolicy />,
      },
      {
        path: PAGE_URL[PageNames.AdBreakPolicy],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      // Ad Break Policy create/view/edit routes
      {
        path: PAGE_URL[PageNames.AdBreakPolicyCreate],
        element: <CreateUpdateControlPolicy />,
      },
      {
        path: PAGE_URL[PageNames.AdBreakPolicyViewEdit],
        element: <CreateUpdateControlPolicy />,
      },
      {
        path: PAGE_URL[PageNames.AdBreakPolicy],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AdBreakPolicyCreate],
        element: <CreateUpdateAdBreakPolicy />,
      },
      {
        path: PAGE_URL[PageNames.AdBreakPolicyViewEdit],
        element: <CreateUpdateAdBreakPolicy />,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Manage Policies Routes End * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Partner Management Routes Start * * * * * * * * * * * * * * * * * *
       */

      {
        path: PAGE_URL[PageNames.Partners],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      { path: PAGE_URL[PageNames.PartnersCreate], element: <CreatePartners /> },
      { path: PAGE_URL[PageNames.PartnersViewEdit], element: <CreatePartners /> },

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Partner Management Routes End * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Bulk Update Routes Start * * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.BlukOperations],
        children: [
          {
            path: '', // Default redirect
            element: <Navigate to={PAGE_URL[PageNames.BlukUpdate]} replace />,
          },
          {
            path: PAGE_URL[PageNames.BlukOperationsListing],
            element: <CommonListing />,
            loader: authenticationGuard,
          },
          { path: PAGE_URL[PageNames.BlukOperationsAddEdit], element: <BulkUpdate /> },
        ],
      },

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Bulk Update Routes End * * * * * * * * * * * * * * * * * *
       */
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Manage Files Start * * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.ManageFiles],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      { path: PAGE_URL[PageNames.ManageFilesAddEdit], element: <ManageFiles /> },
      /**
       * /**
       * * * * * * * * * * * * * * * * * * * * * * * Manage Files End * * * * * * * * * * * * * * * * * *
       */
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Master Routes Start * * * * * * * * * * * * * * * * * *
       */
      { path: PAGE_URL[PageNames.Masters], element: <MastersList />, loader: authenticationGuard },
      {
        path: PAGE_URL[PageNames.MasterTypeList],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.DefaultMasterCreate],
        element: <DefaultMasterEditorPage />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.DefaultMasterEdit],
        element: <DefaultMasterEditorPage />,
        loader: authenticationGuard,
      },
      {
        path: `${MetaMaster}/${Masters}/${CreateSystemConfigurationPageType}/${CreateSystemConfirgurationPage}`,
        element: <CreateSystemConfiguration />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Master Routes End * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Audit Logs Routes Start * * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.AuditLogsUserAndRole],
        element: <CommonAuditLogsList />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsUserAndRoleDetails],
        element: <CommonAuditLogsDetails />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsMaster],
        element: <CommonAuditLogsList />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsMasterDetails],
        element: <CommonAuditLogsDetails />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsTranscoding],
        element: <CommonAuditLogsList />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsTranscodingDetails],
        element: <CommonAuditLogsDetails />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsContent],
        element: <CommonAuditLogsList />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.AuditLogsContentDetails],
        element: <CommonAuditLogsDetails />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Audit Logs Master Routes End * * * * * * * * * * * * * * * * * *
       */

      {
        path: `manage-content/${PAGES_ROUTES.contentCategory}`,
        element: <AssetListingComponent stateDetailsPage={false} />,
        loader: authenticationGuard,
      },

      { path: AccessDenied, element: <AccessDeniedComponent /> },
      { path: ComingSoon, element: <ComingSoonComponent /> },
      { path: InternalServerError, element: <InternalServerErrorComponent /> },
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Manage Tips Routes Start * * * * * * * * * * * * * * * * * *
       */
      {
        path: PAGE_URL[PageNames.ManageTips],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.ManageTipsViewEdit],
        element: <ManageTips />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Manage Tips Routes Start * * * * * * * * * * * * * * * * * *
       */

      /**
       * * * * * * * * * * * * * * * * * * * * * * * Reports Routes Start * * * * * * * * * * * * * * * * * *
       */
      { path: PAGE_URL[PageNames.Reports], element: <ReportsList />, loader: authenticationGuard },
      {
        path: PAGE_URL[PageNames.ContentIngestionReport],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.BulkOperationsReport],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.PublishingMonitoringReport],
        element: <PublishingMonitoringReport />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.SNPReviewReport],
        element: <CommonListing />,
        loader: authenticationGuard,
      },
      {
        path: PAGE_URL[PageNames.SNPReviewReportDetails],
        element: <SNPReviewReportDetails />,
        loader: authenticationGuard,
      },
      /**
       * * * * * * * * * * * * * * * * * * * * * * * Reports Routes End * * * * * * * * * * * * * * * * * *
       */

      { path: '/home/helpdesk', element: <Helpdesk /> },
      { path: AdvancedSearch, element: <SearchAdvanced /> },
      { path: '/', element: <Navigate to='/home' replace /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]

const router = createBrowserRouter(routes)

export default router
